import {
    View,
    Text,
    TextInput,
    StyleSheet,
    useColorScheme,
    Dimensions,
    Keyboard,
    Pressable,
    Platform,
} from "react-native";
import { useTheme } from "@react-navigation/native";
import React, { useCallback, useState } from "react";
import { Controller } from 'react-hook-form';
import Ionicons from "@react-native-vector-icons/ionicons/static";
import CustomLabel from "../CustomLabel/CustomLabel";
var width = Dimensions.get("window").width;

const CustomControlledInput = ({
    control, name, handleFocus,
    rules = {},
    submitEdit, defaultValue, placeholder, secureTextEntry, type = "PRIMARY", maxLen = 1000, editable,
    multiline, numberOfLines, keyboardType, autoCapitalize, clearButtonMode, autoCorrect,
    containerStyle, textStyle, iconName, iconSize = 18, show = "HIDE", selectall = false }) => {

    const { colors } = useTheme();
    const styles = makeStyles(colors);

    const [showPressableBorder, setShowPressableBorder] = useState(false); // show pressable outline when clicking inside textinput

    // * To avoid bug in Web preventing user to type anything in the text box
    function dismissKeyboard() {
        if (Platform.OS != "web") {
            Keyboard.dismiss();
        }
    }

    const handleFocusedInput = () => {
        if (Platform.OS === "web") setShowPressableBorder(true);
        if (!handleFocus) return;
        handleFocus();
    }
    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            key={name}
            render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <>
                    <Pressable
                        disabled={Platform.OS === "web" ? true : false}
                        style={[
                            styles.container,
                            styles[`container_${type}`],
                            containerStyle,
                            // show pressable outline when clicking inside textinput
                            Platform.OS === "web" &&
                            showPressableBorder &&
                            styles.pressable_outline_web,
                        ]}
                        onPress={() => dismissKeyboard()}
                    >
                        <Ionicons
                            name={iconName}
                            size={iconSize}
                            style={[styles[`icon_${show}`]]}
                        />
                        <TextInput
                            value={value}
                            defaultValue={defaultValue}
                            onChangeText={onChange}
                            placeholder={placeholder}
                            selectTextOnFocus={selectall}
                            textContentType='oneTimeCode'
                            placeholderTextColor="#a1a1a1"
                            secureTextEntry={secureTextEntry}
                            onSubmitEditing={submitEdit}
                            maxLength={maxLen}
                            editable={editable}
                            multiline={multiline}
                            numberOfLines={numberOfLines}
                            keyboardType={keyboardType}
                            autoCapitalize={autoCapitalize}
                            clearButtonMode={clearButtonMode}
                            autoCorrect={autoCorrect}
                            onFocus={handleFocusedInput}
                            onBlur={() => {
                                Platform.OS === "web" && setShowPressableBorder(false);
                                onBlur();
                            }}
                            style={[
                                styles.input,
                                styles[`input_${type}`],
                                textStyle,
                                Platform.OS === "web" && { outlineStyle: "none" }, // remove textinput outline, and show pressable outline instead
                            ]}
                        />
                    </Pressable>
                    {error && <CustomLabel type='MINORTITLE_MUTED' value={error?.message} fontSize={12} />
                    }
                </>
            )}
        />
    );
};

const makeStyles = (colors) =>
    StyleSheet.create({
        outerContainer: {
            flex: 1,
            width: '100%'
        },
        container: {
            borderColor: colors.border_color,
            borderWidth: 9,
        },
        input: {
            fontSize: 14,
            fontFamily: "Roboto-Medium",
            color: colors.text_primary,
        },

        container_PRIMARY: {
            flex: 1,
            backgroundColor: colors.background_dropdown,
            borderRadius: 10,
            width: "65%",
            marginLeft: "3%",
            padding: 15,
        },

        input_PRIMARY: {
            flex: 1,
            fontSize: 16,
        },

        container_SEARCH: {
            flex: 1,
            backgroundColor: colors.background_dropdown,
            borderRadius: 5,
            borderColor: "black",
            width: "96%",
            margin: "2%",
            padding: 10,
        },

        input_SEARCH: {
            flex: 1,
            fontSize: 16,
        },

        container_SEARCHPART: {
            flex: 1,
            backgroundColor: colors.background_dropdown,
            borderRadius: 5,
            width: "65%",
            height: "20%",
            marginLeft: "3%",
            padding: 5,
        },

        input_SEARCHPART: {
            flex: 1,
            fontSize: 14,
        },

        container_SHORT: {
            backgroundColor: colors.background_dropdown,
            borderRadius: 10,
            width: "15%",
            height: "6%",
            marginLeft: "3%",
        },

        input_SHORT: {
            flex: 1,
        },

        container_MEDIUM: {
            backgroundColor: colors.background_dropdown,
            borderRadius: 10,
            width: "35%",
            height: "6%",
            marginLeft: "3%",
        },

        input_MEDIUM: {
            height: "90%",
        },

        container_LONG: {
            backgroundColor: colors.background_dropdown,
            borderRadius: 10,
            width: "65%",
            height: "20%",
            marginLeft: "3%",
        },

        input_LONG: {
            height: "100%",
            margin: 24,
            flexWrap: "wrap",
            flexDirection: "row",
        },

        container_LOGIN: {
            backgroundColor: colors.background_dropdown,
            width: '100%',
            height: "6%",
            maxHeight: 50,
            borderColor: "#e8e8e8",
            borderWidth: 1,
            borderRadius: 5,
            marginVertical: 5,
            padding: 10,
            justifyContent: 'center',
        },

        input_LOGIN: {
            color: colors.text_input,
        },
        container_SMALLSQUARE: {
            backgroundColor: colors.background_dropdown,
            flex: 1,
            height: '90%',
            borderColor: colors.border_card,
            borderWidth: 1,
            borderRadius: 5,
            margin: 5,
        },

        input_SMALLSQUARE: {
            flex: 1,
            textAlign: 'center',
            color: colors.text_input,
        },

        container_FULL: {
            flex: 1,
            backgroundColor: colors.background_dropdown,
            borderRadius: 10,
            borderWidth: 2,
            width: '94%',
            marginLeft: '3%',
            marginRight: '3%',
            padding: 7,
            marginBottom: '-1%',
        },

        input_FULL: {
            flex: 1,
            fontSize: 16,
            color: colors.text_primary,
            paddingHorizontal: 5,
        },

        container_PART: {
            flex: 1,
            backgroundColor: colors.background_dropdown,
            borderRadius: 10,
            borderWidth: 2,
            margin: 10,
            marginLeft: 20,
            justifyContent: 'center',
        },

        input_PART: {
            fontSize: 16,
            color: colors.text_primary,
        },

        container_MYPROFILE: {
            flex: 1,
            backgroundColor: colors.background_dropdown,
            borderRadius: 10,
            borderWidth: 2,
            padding: 10,
            height: "100%",
        },

        container_SM_MYPROFILE: {
            flex: 1,
            backgroundColor: colors.background_dropdown,
            borderRadius: 10,
            borderWidth: 2,
            width: "98%",
            height: '100%',
            padding: 10,
        },

        input_SM_MYPROFILE: {
            fontSize: 16,
            color: colors.text_primary,
        },

        input_MYPROFILE: {
            flex: 1,
            fontSize: 16,
            color: colors.text_primary,
            paddingHorizontal: 0,
        },

        container_MULTILINE: {
            flex: 1,
            backgroundColor: colors.background_dropdown,
            borderRadius: 10,
            borderWidth: 2,
            width: "94%",
            margin: "3%",
            padding: 7,
            marginBottom: "-1%",
        },

        input_MULTILINE: {
            flex: 1,
            fontSize: 16,
            paddingHorizontal: 5,
        },

        container_MULTILINE_TIMESHEET: {
            flex: 1,
            backgroundColor: colors.background_dropdown,
            borderRadius: 10,
            borderWidth: 2,
            height: '100%',
            margin: 5,
            padding: 7,
        },

        input_MULTILINE_TIMESHEET: {
            flex: 1,
            fontSize: 16,
            paddingHorizontal: 5,
        },

        container_MULTILINE_ANDROID: {
            flex: 1,
            backgroundColor: colors.background_dropdown,
            borderRadius: 10,
            borderWidth: 2,
            width: '94%',
            margin: '3%',
            padding: 7,
            marginBottom: '-1%',
        },

        input_MULTILINE_ANDROID: {
            flex: 1,
            fontSize: 16,
            verticalAlign: "top",
            paddingHorizontal: 5,
        },

        container_leaveLinesRowFieldText: {
            flex: 1,
            backgroundColor: "#a1a1a1",
            borderRadius: 10,
            width: 60,
            height: 37,
            justifyContent: "center",
            alignItems: "center",
        },

        input_leaveLinesRowFieldText: {
            flex: 1,
            fontSize: 16,
            color: colors.text_primary,
            textAlign: "center",
        },

        icon_SEARCH: {
            color: "#a1a1a1",
            paddingRight: 10,
        },

        icon_HIDE: {
            opacity: 0,
            height: 0,
            width: 0,
        },

        container_SEARCH: {
            backgroundColor: colors.background_dropdown,
            borderRadius: 5,
            borderColor: "black",
            width: "96%",
            margin: "2%",
            padding: 10,
            flex: 1,
            flexDirection: "row",
        },

        container_LAA_Comments: {
            backgroundColor: colors.background_dropdown,
            borderRadius: 10,
            borderWidth: 2,
            width: "94%",
            height: "65%",
            margin: "3%",
        },

        input_LAA_Comments: {
            flex: 1,
            fontSize: 16,
            verticalAlign: "top",
            padding: 5,
            paddingVertical: 10,
        },

        container_SURVEYHEADER1: {
            flex: 1,
            backgroundColor: colors.background_dropdown,
            borderTopWidth: 0,
            borderStartWidth: 0,
            borderEndWidth: 0,
            borderBottomWidth: 2,
            width: "94%",
            marginLeft: "3%",
            marginRight: "1%",
            padding: 10,
            marginBottom: "0%",
        },

        input_SURVEYHEADER1: {
            flex: 1,
            fontSize: 18,
            textAlign: "center",
            color: colors.text_primary,
            paddingHorizontal: 5,
        },

        container_SURVEYHEADER2: {
            flex: 1,
            backgroundColor: colors.background_dropdown,
            borderTopWidth: 0,
            borderStartWidth: 0,
            borderEndWidth: 0,
            borderBottomWidth: 2,
            width: "94%",
            marginLeft: "3%",
            marginRight: "1%",
            padding: 10,
            marginBottom: "0%",
        },

        input_SURVEYHEADER2: {
            flex: 1,
            fontSize: 14,
            textAlign: "center",
            color: colors.text_primary,
            paddingHorizontal: 5,
        },

        container_ASSMLINE: {
            flex: 1,
            width: '150%',
            backgroundColor: colors.background_dropdown,
            borderTopWidth: 0,
            borderStartWidth: 0,
            borderEndWidth: 0,
            borderBottomWidth: 2,
            padding: 10,
        },

        input_ASSMLINE: {
            fontSize: 14,
            textAlign: "center",
            color: colors.text_primary,
        },

        container_QUESTION: {
            flex: 1,
            backgroundColor: colors.background_dropdown,
            borderTopWidth: 0,
            borderStartWidth: 0,
            borderEndWidth: 0,
            borderBottomWidth: 2,
            width: "94%",
            marginLeft: "3%",
            marginRight: "1%",
            padding: 10,
            marginBottom: "0%",
        },

        input_QUESTION: {
            flex: 1,
            fontSize: 16,
            textAlign: "center",
            color: "gray",
            paddingHorizontal: 5,
        },
        container_SURVEYHEADER3: {
            flex: 1,
            backgroundColor: colors.background_dropdown,
            borderTopWidth: 0,
            borderStartWidth: 0,
            borderEndWidth: 0,
            borderBottomWidth: 0,
            width: "94%",
            marginLeft: "3%",
            marginRight: "1%",
            padding: 10,
            marginBottom: "0%",
        },

        input_SURVEYHEADER3: {
            flex: 1,
            fontSize: 18,
            textAlign: "center",
            color: colors.text_primary,
            paddingHorizontal: 5,
        },

        pressable_outline_web: {
            borderColor: colors.text_secondary,
            borderWidth: 1,
            borderRadius: 10,
        },

        container_FULL_INLINE: {
            flex: 1,
            width: "100%",
            height: "6%",
            backgroundColor: colors.background_dropdown,
            borderRadius: 10,
            borderWidth: 2,
            padding: 7,
            marginVertical: 0,
            marginBottom: "1%",
        },

        input_FULL_INLINE: {
            flex: 1,
            fontSize: 16,
            color: colors.text_primary,
            paddingHorizontal: 5,
        },

        container_MULTILINE_LEAVE_MESSAGE: {
            flex: 1,
            backgroundColor: colors.background_approved,
            borderRadius: 10,
            borderWidth: 2,
            width: Platform.OS === "web" && width > 1300 ? "64%" : "94%",
            padding: 7,
            margin: "3%",
            marginTop: Platform.OS === "web" && width > 1300 ? "3%" : "6%",
            marginBottom: "-2%",
        },

        input_MULTILINE_LEAVE_MESSAGE: {
            flex: 1,
            fontSize: 16,
            verticalAlign: "top",
            paddingHorizontal: 5,
            textAlign: "justify",
        },

        container_NEW_COMPANY: {
            backgroundColor: colors.background_dropdown,
            borderRadius: 10,
            borderWidth: 2,
            flex: 1,
            padding: 15,
        },

        input_NEW_COMPANY: {
            flex: 1,
            fontSize: 16,
        },

        container_BRANCHES: {
            borderWidth: 2,
            elevation: 5,
            shadowColor: colors.card_line,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.3,
            shadowRadius: 5,
            flex: 1,
            borderRadius: 10,
            width: "90%",
            marginLeft: "3%",
            padding: 15,
        },

        input_BRANCHES: {
            flex: 1,
            fontSize: 20,
        },
        container_MISSING_BRANCHES_CODE: {
            borderWidth: 2,
            shadowColor: colors.background_rejected,
            shadowRadius: 5,
            flex: 1,
            borderRadius: 10,
            width: "90%",
            marginLeft: "3%",
            padding: 15,
            borderColor: colors.background_rejected
        },

        input_MISSING_BRANCHES_CODE: {
            flex: 1,
            fontSize: 20,
        },

        container_SELECT_EMPLOYEE: {
            backgroundColor: colors.background_dropdown,
            borderRadius: 10,
            borderWidth: 2,
            padding: 10,
            margin: '2%',
            borderColor: colors.border_card,
        },

        input_SELECT_EMPLOYEE: {
            fontSize: 16,
            color: colors.text_primary,
            paddingHorizontal: 0,
        },

        container_EDIT_EMPLOYEE: {
            backgroundColor: colors.background_dropdown,
            borderRadius: 10,
            borderWidth: 2,
            flex: 1,
            padding: 7,
            margin: 5,
        },
        input_EDIT_EMPLOYEE: {
            flex: 1,
            fontSize: 16,
        },
        error_EDIT_EMPLOYEE: {
            flex: 1,
            padding: 7,
            margin: 5,
        },
        container_EDIT_EMPLOYEE_IMPORTANT: {
            backgroundColor: colors.background_approved,
            borderColor: colors.border_card,
            borderRadius: 10,
            borderWidth: 2,
            flex: 1,
            padding: 15,
            margin: 5,
        },
        input_EDIT_EMPLOYEE_IMPORTANT: {
            flex: 1,
            color: colors.text_secondary,
            fontSize: 16,
        },
        error_EDIT_EMPLOYEE_IMPORTANT: {
            flex: 1,
            padding: 7,
            margin: 5,
        },
        container_MAININPUT: {
            flex: 1,
            backgroundColor: 'transparent',
            borderWidth: 0,
            borderBottomWidth: 1.5,
            borderBottomColor: colors.green_dark,
            borderRadius: 0,
            paddingVertical: 8,
            paddingHorizontal: 4,
            marginHorizontal: 15,
        },

        input_MAININPUT: {
            fontSize: 17,
            fontWeight: '600',
        },

        error_MAININPUT: {
            flex: 1,
            padding: 10,
            margin: 5,
        },


        container_EDIT_EMPLOYEE_REQUIRED: {
            backgroundColor: colors.background_dropdown,
            borderColor: colors.background_rejected,
            borderRadius: 10,
            borderWidth: 2,
            flex: 1,
            width: "100%",
            padding: 7,
            margin: 5,
        },
        input_EDIT_EMPLOYEE_REQUIRED: {
            flex: 1,
            fontSize: 16,
        },
        error_EDIT_EMPLOYEE_REQUIRED: {
            flex: 1,
            width: "100%",
            padding: 7,
            margin: 5,
        },

        container_EDIT_EMPLOYEE_DATE: {
            backgroundColor: colors.background_dropdown,
            borderRadius: 10,
            borderWidth: 2,
            flex: 1,
            width: "100%",
            padding: 7,
            margin: 5,
        },
        input_EDIT_EMPLOYEE_DATE: {
            flex: 1,
            fontSize: 16,
        },
        container_EDIT_EMPLOYEE_FAMILY: {
            backgroundColor: colors.background_dropdown,
            borderRadius: 10,
            borderWidth: 2,
            flex: 1,
            marginLeft: "3%",
            padding: 7,
            margin: 5,
        },
        input_EDIT_EMPLOYEE_FAMILY: {
            flex: 1,
            fontSize: 16,
        },
    });

export default CustomControlledInput;
